import React, { useState } from 'react';
import { SlidersHorizontal, Link } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTrigger,
} from "./sheet";
import { Button } from "./button";
import { Label } from "./label";
import { Slider } from "./slider";
import { Checkbox } from "./checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./Select";
import { CSS4_COLORS } from './mplcolors';
import { Bike, Building2, Trees, Mountain, Waves, Droplets, Car, LandPlot } from 'lucide-react';
import { Input } from "./input";

const ACTIVITY_TYPES = ['Run', 'Ride', 'Hike', 'Walk', 'Swim', 'TrailRun', 'VirtualRide'];

const ColorSettingsPopup = ({
  settings,
  onColorSettingChange,
  onActivityTypeSettingChange,
  triggerButton,
  syncOceansWater,
  onSyncToggle,
  activitySource,
  selectedCategory: selectedCategoryProp,
  onSelectedCategoryChange,
}) => {
  const categories = ['activities', 'land', 'buildings', 'streets', 'forests', 'naturalareas', 'oceans', 'water'];
  const streetType = ["roads", "all"];
  const streetType_reduced = ["roads"];
  const [sync, setSync] = useState(false || syncOceansWater);
  const [activityTab, setActivityTab] = useState(ACTIVITY_TYPES[0]);
  const [selectedCategoryLocal, setSelectedCategoryLocal] = useState(null);
  const selectedCategory = selectedCategoryProp !== undefined ? selectedCategoryProp : selectedCategoryLocal;
  const setSelectedCategory = onSelectedCategoryChange || setSelectedCategoryLocal;
  const resolveHex = (colorStr) => {
    if (!colorStr) return '#cccccc';
    if (colorStr.startsWith('#')) return colorStr;
    return CSS4_COLORS[colorStr] || '#cccccc';
  };

  // The header background is drawn semi-transparent on a white card; compute
  // the blended color for a contrast check so text is readable even when alpha
  // is low (otherwise white-on-nearly-white).
  const getHeaderColors = (hex, alpha) => {
    const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
    const a = Math.max(0, Math.min(1, typeof alpha === 'number' ? alpha : 1));
    if (!m) return { bg: hex, fg: '#111827' };
    const r = parseInt(m[1], 16);
    const g = parseInt(m[2], 16);
    const b = parseInt(m[3], 16);
    const bg = `rgba(${r}, ${g}, ${b}, ${a})`;
    const br = r * a + 255 * (1 - a);
    const bgBl = g * a + 255 * (1 - a);
    const bb = b * a + 255 * (1 - a);
    const lum = (0.299 * br + 0.587 * bgBl + 0.114 * bb) / 255;
    return { bg, fg: lum > 0.6 ? '#111827' : '#ffffff' };
  };

  const categoryIcons = {
    activities: Bike,
    land: LandPlot,
    buildings: Building2,
    forests: Trees,
    naturalareas: Mountain,
    oceans: Waves,
    water: Droplets,
    streets: Car,
  };

  // Opacity "sweet spots" per category — rendered as a subtle green band on
  // the opacity slider so the user can see the recommended range at a glance.
  // Rationale: too-high land/forest coverage hides the topography underneath.
  const opacityOptimalRanges = {
    activities: [50, 100],
    land: [5, 60],
    forests: [5, 45],
    oceans: [10, 100],
    water: [10, 100],
    buildings: [5, 100],
    streets: [10, 80],
  };

  const handleCustomColorClick = (category) => {
    const input = document.createElement('input');
    input.type = 'color';
    input.value = settings[category].color;
    input.addEventListener('change', (e) => {
      onColorSettingChange(category, 'color', e.target.value);
      if (sync && category === 'oceans') {
        onColorSettingChange('water', 'color', e.target.value);
      }
    });
    input.click();
  };

  const getDisplayValue = (color) => {
    if (color && color.startsWith('#')) return `custom: ${color}`;
    return color;
  };

  const handleSliderChange = (category, value) => {
    onColorSettingChange(category, 'alpha', value[0] / 100);
    if (sync && category === 'oceans') {
      onColorSettingChange('water', 'alpha', value[0] / 100);
    }
  };

  const handleOpacityInputChange = (category, value) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      onColorSettingChange(category, 'alpha', numValue / 100);
      if (sync && category === 'oceans') {
        onColorSettingChange('water', 'alpha', numValue / 100);
      }
    }
  };

  const handleColorChange = (category, color) => {
    onColorSettingChange(category, 'color', color);
    if (sync && category === 'oceans') {
      onColorSettingChange('water', 'color', color);
    }
  };

  // Open a native color picker that writes back through an arbitrary setter —
  // used for per-activity-type tabs so the "custom" option persists to byType.
  const openCustomColorPicker = (initialColor, setColor) => {
    const input = document.createElement('input');
    input.type = 'color';
    input.value = initialColor && initialColor.startsWith('#') ? initialColor : '#ff0000';
    input.addEventListener('change', (e) => setColor(e.target.value));
    input.click();
  };

  // Render the shared color / opacity / line-width controls from a resolved
  // scope and a single write function. Used for both the common activities
  // settings and per-type overrides.
  const renderActivityStyleControls = ({ color, alpha, onChange }) => {
    return (
      <div className="space-y-3">
        <div className="space-y-1">
          <Label className="text-xs">Color</Label>
          <Select
            value={color}
            onValueChange={(value) => {
              if (value === 'custom') {
                openCustomColorPicker(color, (c) => onChange('color', c));
                return;
              }
              onChange('color', value);
            }}
          >
            <SelectTrigger className="bg-white border border-none rounded-lg h-9 text-sm">
              <SelectValue>
                <div className="flex items-center">
                  <div className="w-4 h-4 mr-2 rounded-full" style={{ backgroundColor: color }} />
                  {getDisplayValue(color)}
                </div>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <div className="max-h-96 overflow-y-auto">
                <SelectItem
                  value="custom"
                  className="sticky top-0 z-10 bg-white border-b border-gray-200"
                >
                  <div className="flex items-center">
                    <div
                      className="w-4 h-4 mr-2 rounded-full"
                      style={{ background: 'linear-gradient(to right, red,orange,yellow,green,blue,indigo,violet)' }}
                    />
                    custom
                  </div>
                </SelectItem>
                {Object.keys(CSS4_COLORS).map((c) => (
                  <SelectItem key={c} value={c}>
                    <div className="flex items-center">
                      <div className="w-4 h-4 mr-2 rounded-full" style={{ backgroundColor: c }} />
                      {c}
                    </div>
                  </SelectItem>
                ))}
              </div>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Opacity ({Math.round(alpha * 100)}%)</Label>
          <div className="flex items-center gap-2">
            <div className="flex-grow relative">
              {opacityOptimalRanges.activities && (
                <div
                  className="pointer-events-none absolute top-1/2 -translate-y-1/2 h-7 bg-green-200/70 rounded-md z-0"
                  style={{
                    left: `${opacityOptimalRanges.activities[0]}%`,
                    width: `${opacityOptimalRanges.activities[1] - opacityOptimalRanges.activities[0]}%`,
                  }}
                  aria-hidden="true"
                />
              )}
              <Slider
                value={[alpha * 100]}
                min={0}
                max={100}
                step={1}
                onValueChange={(value) => onChange('alpha', value[0] / 100)}
                className="relative z-10 border-2 border-white rounded-lg"
              />
            </div>
            <Input
              type="number"
              min={0}
              max={100}
              value={Math.round(alpha * 100)}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                if (!isNaN(n) && n >= 0 && n <= 100) onChange('alpha', n / 100);
              }}
              className="w-14 h-8 text-center text-sm"
            />
          </div>
          {opacityOptimalRanges.activities && (
            <div className="text-[10px] text-green-700">
              Optimal in Range: {opacityOptimalRanges.activities[0]}–{opacityOptimalRanges.activities[1]}%
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderActivityBody = () => {
    const activities = settings.activities || {};
    const byTypeEnabled = !!activities.byTypeEnabled;

    const commonScope = {
      color: activities.color,
      alpha: activities.alpha,
    };

    const commonOnChange = (setting, value) => onColorSettingChange('activities', setting, value);

    const canEditPerType = byTypeEnabled && typeof onActivityTypeSettingChange === 'function';
    const typeOverrides = activities.byType?.[activityTab] || {};
    const typeScope = {
      color: typeOverrides.color || activities.color,
      alpha: typeOverrides.alpha != null ? typeOverrides.alpha : activities.alpha,
    };
    const typeOnChange = (setting, value) => onActivityTypeSettingChange(activityTab, setting, value);

    return (
      <>
        {typeof onActivityTypeSettingChange === 'function' && (
          <div className="flex items-center justify-end gap-2 mb-2">
            <Label htmlFor="byTypeEnabled" className="text-xs cursor-pointer">
              Choose different colors for activities
            </Label>
            <Checkbox
              id="byTypeEnabled"
              checked={byTypeEnabled}
              onCheckedChange={(checked) => onColorSettingChange('activities', 'byTypeEnabled', !!checked)}
              className="h-5 w-5 rounded-full border-black"
            />
          </div>
        )}

        {canEditPerType && (
          <div className="-mx-2 px-2 border-b border-gray-300 mb-2">
            <div className="flex flex-wrap gap-1">
              {ACTIVITY_TYPES.map((type) => {
                const isActive = activityTab === type;
                const hasOverride = !!activities.byType?.[type];
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setActivityTab(type)}
                    className={`px-2 py-1 text-xs whitespace-nowrap rounded-t-lg border border-b-0 ${isActive
                      ? 'bg-white font-semibold border-gray-300'
                      : 'bg-transparent border-transparent text-gray-600 hover:bg-gray-100'
                      }`}
                  >
                    {type}
                    {hasOverride && <span className="ml-1 text-[10px] text-blue-600">●</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {canEditPerType ? (
          <>
            {renderActivityStyleControls({
              ...typeScope,
              onChange: typeOnChange,
            })}
            {activities.byType?.[activityTab] && (
              <div className="mt-2 text-right">
                <button
                  type="button"
                  onClick={() => onActivityTypeSettingChange(activityTab, '__reset__', null)}
                  className="text-xs text-gray-500 hover:text-gray-800 underline"
                >
                  Reset {activityTab} to common
                </button>
              </div>
            )}
          </>
        ) : (
          renderActivityStyleControls({
            ...commonScope,
            onChange: commonOnChange,
          })
        )}
      </>
    );
  };

  const getCategoryMeta = (category) => {
    const isActive = !!settings[category].active;
    const isWaterSynced = sync && category === 'water';
    const isActivitiesDisabled = category === 'activities' && activitySource === 'none';
    const isBuildingsBig = category === 'buildings' && settings.km2 > 15000;
    const isStreetsBig = category === 'streets' && settings.km2 > 15000;
    const blocked = isWaterSynced || isActivitiesDisabled;
    const toggleDisabled = blocked || isBuildingsBig || isStreetsBig;
    const hex = resolveHex(settings[category].color);
    const showColored = isActive && !blocked;
    const headerColors = showColored
      ? getHeaderColors(hex, settings[category].alpha)
      : { bg: '#9ca3af', fg: '#ffffff' };
    return {
      isActive,
      isWaterSynced,
      isActivitiesDisabled,
      isBuildingsBig,
      isStreetsBig,
      blocked,
      toggleDisabled,
      hex,
      headerBg: headerColors.bg,
      headerFg: headerColors.fg,
    };
  };

  const renderCategoryHeader = (category) => {
    const meta = getCategoryMeta(category);
    const Icon = categoryIcons[category];
    const showLink = sync && (category === 'oceans' || category === 'water');
    return (
      <div
        className="px-2 py-1.5 flex items-center gap-2"
        style={{ backgroundColor: meta.headerBg, color: meta.headerFg }}
      >
        <Icon className="h-4 w-4 flex-shrink-0" />
        <span className="text-sm font-semibold capitalize truncate">{category}</span>
        {showLink && (
          <Link className="h-3.5 w-3.5 flex-shrink-0" title="Linked with water / oceans" />
        )}
        <div
          className="ml-auto flex items-center"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={meta.isActive}
            disabled={meta.toggleDisabled}
            onCheckedChange={(checked) => {
              onColorSettingChange(category, 'active', checked);
              if (sync && category === 'oceans') {
                onColorSettingChange('water', 'active', checked);
              }
            }}
            className="h-5 w-5 rounded-full border-current"
          />
        </div>
      </div>
    );
  };

  const renderCategoryTile = (category) => {
    const meta = getCategoryMeta(category);

    let titleHint = `Edit ${category}`;
    if (meta.isActivitiesDisabled) titleHint = 'Select an activity source';
    else if (meta.isWaterSynced) titleHint = 'Synced with oceans';
    else if (meta.isBuildingsBig || meta.isStreetsBig) titleHint = 'Area too large';

    const handleClick = () => setSelectedCategory(category);

    return (
      <div
        role="button"
        tabIndex={meta.blocked ? -1 : 0}
        onClick={() => { if (!meta.blocked) handleClick(); }}
        onKeyDown={(e) => {
          if (!meta.blocked && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleClick();
          }
        }}
        className={`h-full flex flex-col overflow-hidden rounded-xl border shadow-sm transition group ${meta.blocked
          ? 'opacity-60 cursor-not-allowed border-gray-300'
          : 'cursor-pointer hover:shadow-md hover:scale-[1.02] border-transparent'
          } ${!meta.isActive && !meta.blocked ? 'saturate-50' : ''}`}
        title={titleHint}
      >
        {renderCategoryHeader(category)}
        <div className={`relative flex-1 aspect-[4/3] bg-gray-100 overflow-hidden ${!meta.isActive ? 'grayscale opacity-80' : ''}`}>
          <img
            src={`/assets/categories/${category}.png`}
            alt={category}
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
          />
          {!meta.blocked && (
            <div className="absolute bottom-2 right-2 bg-white bg-opacity-90 rounded-full p-1.5 shadow-md opacity-80 group-hover:opacity-100 transition-opacity">
              <SlidersHorizontal className="h-3.5 w-3.5 text-gray-700" />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCollapsedTile = (category) => {
    const meta = getCategoryMeta(category);
    const handleClick = () => setSelectedCategory(category);
    let titleHint = `Open ${category}`;
    if (meta.isActivitiesDisabled) titleHint = 'Select an activity source';
    else if (meta.isWaterSynced) titleHint = 'Synced with oceans';
    else if (meta.isBuildingsBig || meta.isStreetsBig) titleHint = 'Area too large';

    return (
      <div
        role="button"
        tabIndex={meta.blocked ? -1 : 0}
        onClick={() => { if (!meta.blocked) handleClick(); }}
        onKeyDown={(e) => {
          if (!meta.blocked && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleClick();
          }
        }}
        className={`rounded-xl overflow-hidden border shadow-sm transition ${meta.blocked
          ? 'opacity-60 cursor-not-allowed border-gray-300'
          : 'cursor-pointer hover:shadow-md border-transparent'
          } ${!meta.isActive && !meta.blocked ? 'saturate-50' : ''}`}
        title={titleHint}
      >
        {renderCategoryHeader(category)}
      </div>
    );
  };

  const renderExpandedTile = (category) => {
    const meta = getCategoryMeta(category);
    const sizeWarning = meta.isBuildingsBig
      ? 'Buildings disabled for areas bigger than 15000 km²'
      : meta.isStreetsBig
        ? 'Streets disabled for areas bigger than 15000 km²'
        : null;

    return (
      <div className="flex flex-col overflow-hidden rounded-xl border border-gray-200 shadow-lg bg-white">
        {renderCategoryHeader(category)}
        <div className="p-3 space-y-2">
          {meta.isWaterSynced && (
            <div className="bg-blue-100 text-blue-800 px-3 py-2 rounded-xl text-sm flex items-center gap-2 justify-center">
              <Link className="h-4 w-4" />
              <span className="font-medium">Synced with oceans — controlled by the oceans category</span>
            </div>
          )}
          {meta.isActivitiesDisabled && (
            <div className="text-sm text-gray-600 text-center py-4">
              Select an activity source to enable activities.
            </div>
          )}
          {sizeWarning && (
            <div className="text-red-500 text-sm text-center">{sizeWarning}</div>
          )}
          {meta.isActive && !meta.isActivitiesDisabled && !meta.isWaterSynced ? (
            category === 'activities' ? renderActivityBody() : renderNonActivityBody(category)
          ) : (!meta.isActivitiesDisabled && !meta.isWaterSynced && (
            <div className="text-sm text-gray-500 text-center py-4">
              Turn on the toggle in the header to edit settings.
            </div>
          ))}
        </div>
        <div className="border-t bg-gray-50">
          <Button
            onClick={() => setSelectedCategory(null)}
            className="w-full bg-gray-900 border-0 text-white hover:bg-gray-600 rounded-none"
          >
            Done
          </Button>
        </div>
      </div>
    );
  };

  const renderNonActivityBody = (category) => (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label className="text-xs">Color</Label>
        <Select
          value={settings[category].color}
          onValueChange={(value) => {
            if (value === 'custom') {
              handleCustomColorClick(category);
              return;
            }
            handleColorChange(category, value);
          }}
        >
          <SelectTrigger className="bg-white border border-none rounded-lg h-9 text-sm">
            <SelectValue>
              <div className="flex items-center">
                <div
                  className="w-4 h-4 mr-2 rounded-full"
                  style={{ backgroundColor: settings[category].color }}
                />
                {getDisplayValue(settings[category].color)}
              </div>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <div className="max-h-96 overflow-y-auto">
              <SelectItem
                value="custom"
                className="sticky top-0 z-10 bg-white border-b border-gray-200"
              >
                <div className="flex items-center">
                  <div
                    className="w-4 h-4 mr-2 rounded-full"
                    style={{ background: 'linear-gradient(to right, red,orange,yellow,green,blue,indigo,violet)' }}
                  />
                  custom
                </div>
              </SelectItem>
              {Object.keys(CSS4_COLORS).map((color) => (
                <SelectItem key={color} value={color}>
                  <div className="flex items-center">
                    <div
                      className="w-4 h-4 mr-2 rounded-full"
                      style={{ backgroundColor: color }}
                    />
                    {color}
                  </div>
                </SelectItem>
              ))}
            </div>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Opacity ({Math.round(settings[category].alpha * 100)}%)</Label>
        <div className="flex items-center gap-2">
          <div className="flex-grow relative">
            {opacityOptimalRanges[category] && (
              <div
                className="pointer-events-none absolute top-1/2 -translate-y-1/2 h-7 bg-green-200/70 rounded-md z-0"
                style={{
                  left: `${opacityOptimalRanges[category][0]}%`,
                  width: `${opacityOptimalRanges[category][1] - opacityOptimalRanges[category][0]}%`,
                }}
                aria-hidden="true"
              />
            )}
            <Slider
              value={[settings[category].alpha * 100]}
              min={0}
              max={100}
              step={1}
              onValueChange={(value) => handleSliderChange(category, value)}
              className="relative z-10 border-2 border-white rounded-lg"
            />
          </div>
          <Input
            type="number"
            min={0}
            max={100}
            value={Math.round(settings[category].alpha * 100)}
            onChange={(e) => handleOpacityInputChange(category, e.target.value)}
            className="w-14 h-8 text-center text-sm"
          />
        </div>
        {opacityOptimalRanges[category] && (
          <div className="text-[12px] text-green-500">
            Optimal in Range: {opacityOptimalRanges[category][0]}–{opacityOptimalRanges[category][1]}%
          </div>
        )}
      </div>
      {category === 'streets' && (
        <div className="space-y-1">
          <Label className="text-xs">Street details</Label>
          <Select
            value={settings.streets.details}
            onValueChange={(value) => onColorSettingChange('streets', 'details', value)}
          >
            <SelectTrigger className="bg-white border border-none rounded-lg h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(settings.km2 <= 4000 ? streetType : streetType_reduced).map((details) => (
                <SelectItem key={details} value={details}>
                  {details}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {settings.km2 > 4000 && (
            <div className="text-red-500 text-xs">Only main roads can be chosen for areas bigger than 4000 km²</div>
          )}
        </div>
      )}
      {category === 'oceans' && (
        <div className="flex items-center gap-2 pt-1">
          <Checkbox
            checked={sync}
            id="syncWaterCheckbox"
            onCheckedChange={(checked) => {
              if (onSyncToggle) onSyncToggle(checked);
              setSync(checked);
              if (checked) {
                onColorSettingChange('water', 'color', settings.oceans.color);
                onColorSettingChange('water', 'alpha', settings.oceans.alpha);
                onColorSettingChange('water', 'active', settings.oceans.active);
              }
            }}
            className="h-5 w-5 rounded-full border-black"
          />
          <Label htmlFor="syncWaterCheckbox" className="text-xs flex items-center gap-1">
            Sync water <Link className="h-3 w-3" />
          </Label>
        </div>
      )}
    </div>
  );

  const renderContent = () => {
    if (selectedCategory) {
      const others = categories.filter((c) => c !== selectedCategory);
      return (
        <div className="flex gap-3 py-2">
          <div className="flex flex-col gap-2 w-28 md:w-44 shrink-0">
            {others.map((c) => (
              <div key={c}>{renderCollapsedTile(c)}</div>
            ))}
          </div>
          <div className="flex-1 min-w-0">
            <div className="w-full max-w-xl mx-auto">
              {renderExpandedTile(selectedCategory)}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-4 gap-3 py-2">
        {categories.map((category) => (
          <div key={category}>{renderCategoryTile(category)}</div>
        ))}
      </div>
    );
  };

  if (triggerButton) {
    return (
      <Sheet>
        <SheetTrigger asChild>
          <Button
            size="sm"
            className="text-xs lg:text-md rainbow-border-bg text-white hover:bg-gray-600"
          >
            <SlidersHorizontal className="h-4 w-4 mr-2" />
            Customize
          </Button>
        </SheetTrigger>
        <SheetContent className="w-full overflow-y-auto">
          <SheetHeader>
            {/* <SheetTitle>Color Settings</SheetTitle> */}
          </SheetHeader>
          {renderContent()}
        </SheetContent>
      </Sheet>
    );
  }

  return renderContent();
};

export default ColorSettingsPopup;
